/**
 * AdminPortalPage (AC-921)
 *
 * Admin portal with tabbed interface:
 * - "Email Allowlist" tab: AddEmailForm + AllowlistTable
 * - "Settings" tab: RegistrationModeToggle
 * - "Users" tab: AdminUserList
 *
 * Uses ARIA tab pattern: role="tablist", role="tab", aria-selected, role="tabpanel"
 * Follows the pattern from MyTeamsPage.tsx.
 *
 * Route: /admin
 * Parent: Layout via Outlet
 * User Access: Sidebar "Admin" link (admin only)
 *
 * Epic: AC-915 - Soft-Launch Email Allowlist with Admin Portal
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { useAuthStore } from '../store';
import AllowlistTable from '../components/admin/AllowlistTable';
import AddEmailForm from '../components/admin/AddEmailForm';
import RegistrationModeToggle from '../components/admin/RegistrationModeToggle';
import AdminUserList from '../components/admin/AdminUserList';
import type { AdminFilterValue } from '../components/admin/AdminUserList';
import AdminTokenUsage from '../components/admin/AdminTokenUsage';

type TabKey = 'allowlist' | 'settings' | 'users' | 'token-usage';

const AdminPortalPage: React.FC = () => {
  const { user } = useAuthStore();
  const {
    allowlist,
    allowlistTotal,
    allowlistLoading,
    registrationMode,
    registrationModeLoading,
    users,
    usersTotal,
    usersLoading,
    fetchAllowlist,
    fetchRegistrationMode,
    fetchUsers,
    addEmail,
    addEmails,
    removeEmail,
    setRegistrationMode,
    setUserRole,
  } = useAdminStore();

  const [activeTab, setActiveTab] = useState<TabKey>('allowlist');
  const [allowlistSearch, setAllowlistSearch] = useState('');
  const [allowlistPage, setAllowlistPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersAdminFilter, setUsersAdminFilter] = useState<AdminFilterValue>('all');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Load data on mount
  useEffect(() => {
    fetchAllowlist();
    fetchRegistrationMode();
  }, [fetchAllowlist, fetchRegistrationMode]);

  // Debounced search for users
  const handleUsersSearchChange = useCallback((query: string) => {
    setUsersSearch(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      const is_admin = usersAdminFilter === 'all' ? undefined : usersAdminFilter === 'admin';
      fetchUsers({ search: query || undefined, is_admin });
    }, 300);
  }, [fetchUsers, usersAdminFilter]);

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    // Lazy-load users data when switching to users tab
    if (tab === 'users' && users.length === 0) {
      fetchUsers();
    }
  };

  const handleAdminFilterChange = (filter: AdminFilterValue) => {
    setUsersAdminFilter(filter);
    const is_admin = filter === 'all' ? undefined : filter === 'admin';
    fetchUsers({ search: usersSearch || undefined, is_admin });
  };

  const handleAddSingle = async (email: string) => {
    await addEmail(email);
  };

  const handleAddBulk = async (emails: string[]) => {
    await addEmails(emails);
  };

  const handleRemove = (entryId: number) => {
    removeEmail(entryId);
  };

  const handleSetMode = async (mode: 'open' | 'invite_only') => {
    await setRegistrationMode(mode);
  };

  const handleRoleChange = async (userId: number, isAdmin: boolean) => {
    await setUserRole(userId, isAdmin);
    // Re-fetch to keep the filtered list accurate (e.g. demoted admin leaves "Admins Only" view)
    const is_admin = usersAdminFilter === 'all' ? undefined : usersAdminFilter === 'admin';
    fetchUsers({ search: usersSearch || undefined, is_admin });
  };

  const handleDeleteUser = async (userId: number) => {
    const { deleteUser } = useAdminStore.getState();
    await deleteUser(userId);
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'allowlist', label: 'Email Allowlist' },
    { key: 'settings', label: 'Settings' },
    { key: 'users', label: 'Users' },
    { key: 'token-usage', label: 'Token Usage' },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck className="w-7 h-7 text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-warm-white">
          Admin Portal
        </h1>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Panels */}
      {activeTab === 'allowlist' && (
        <div role="tabpanel" id="allowlist-panel">
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-4">
                Add Emails
              </h2>
              <AddEmailForm
                onAddSingle={handleAddSingle}
                onAddBulk={handleAddBulk}
                isLoading={allowlistLoading}
              />
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-4">
                Allowlist ({allowlistTotal})
              </h2>
              <AllowlistTable
                entries={allowlist}
                total={allowlistTotal}
                isLoading={allowlistLoading}
                searchQuery={allowlistSearch}
                onSearchChange={setAllowlistSearch}
                onRemove={handleRemove}
                onPageChange={setAllowlistPage}
                currentPage={allowlistPage}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div role="tabpanel" id="settings-panel">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <RegistrationModeToggle
              currentMode={registrationMode}
              onToggle={handleSetMode}
              isLoading={registrationModeLoading}
            />
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div role="tabpanel" id="users-panel">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-4">
              User Management
            </h2>
            <AdminUserList
              users={users}
              total={usersTotal}
              isLoading={usersLoading}
              currentUserId={user ? user.user_id : 0}
              onRoleChange={handleRoleChange}
              onDeleteUser={handleDeleteUser}
              searchQuery={usersSearch}
              onSearchChange={handleUsersSearchChange}
              adminFilter={usersAdminFilter}
              onAdminFilterChange={handleAdminFilterChange}
            />
          </div>
        </div>
      )}

      {activeTab === 'token-usage' && (
        <div role="tabpanel" id="token-usage-panel">
          <AdminTokenUsage />
        </div>
      )}
    </div>
  );
};

export default AdminPortalPage;
